import type { FilterGroupRequest } from './contactList'

// =====================================================================
// REQUEST TYPES
// =====================================================================

export interface BoundingBox {
  minLng: number
  minLat: number
  maxLng: number
  maxLat: number
}

// Filter-group payload shared by the universe/live-metrics + list endpoints.
export type GeoFilterGroups = FilterGroupRequest[]

export interface AnalyticsContactsRequest {
  filterGroups: FilterGroupRequest[]
  bBox?: BoundingBox
  page?: number
  pageSize?: number
}

export interface AnalyticsDashboardRequest {
  filterGroups: FilterGroupRequest[]
  polygonWkt?: string
  bBox?: BoundingBox
}

export interface AnalyticsHighValueRequest {
  filterGroups: FilterGroupRequest[]
  profileName: string
  bBox?: BoundingBox
  topN?: number
}

export interface AnalyticsExportRequest {
  filterGroups: FilterGroupRequest[]
  polygonWkt?: string
  label: string
}

// =====================================================================
// RESPONSE TYPES
// =====================================================================

export interface EnrichedContactLocation {
  id: string
  firstName: string
  lastName: string
  streetAddress: string
  latitude: number
  longitude: number
  ideologyPlus?: number
  voteProp?: number
  activism?: number
  raceModel?: string
  party?: string
  ageBin?: string
  contactStatus: number
}

export interface DashboardData {
  totalContacts: number
  ideologyDistribution: Record<string, number>
  partyBreakdown: Record<string, number>
  raceBreakdown: Record<string, number>
  ageDistribution: Record<string, number>
  turnoutDistribution: Record<string, number>
}

export interface HighValueVoter {
  contactId: string
  lat: number
  lng: number
  priorityScore: number
  profileName: string
  name: string
  address: string
  flagKeys?: string[]
}

export interface ListFiltersResponse {
  filterGroups: FilterGroupRequest[]
  isMaterialized: boolean
  label: string
}

export interface ScoringProfile {
  name: string
  label: string
  description: string
  plainSummary: string
  category: 'default' | 'advanced'
  defaultThreshold: number
}

// =====================================================================
// MULTI-PROFILE HIGH-VALUE VOTERS
// =====================================================================

export interface MultiProfileHighValueRequest {
  filterGroups: FilterGroupRequest[]
  profileNames: string[]
  bBox?: BoundingBox
  topN?: number
  thresholds?: Record<string, number>
}

export const SCORING_PROFILE_COLORS: Record<string, string> = {
  mobilization_target: '#EF4444',
  swing_voter: '#8B5CF6',
  untapped_activist: '#10B981',
  blue_voter_red_district: '#F59E0B',
  red_voter_blue_district: '#84CC16',
  issue_aligned_passive: '#EC4899',
  canvass_priority: '#14B8A6',
  impact_gotv: '#6366F1',
  impact_primary: '#A855F7',
  impact_persuasion: '#0EA5E9',
}

// =====================================================================
// STACKABLE CHART PANELS
// =====================================================================

export type ChartType = 'ideology' | 'party' | 'race' | 'age' | 'turnout'

export interface ChartPanelConfig {
  id: string
  chartType: ChartType
}

// =====================================================================
// SCOPE SELECTION
// =====================================================================

export type ScopeType = 'visible' | 'highValue' | 'polygon'

// =====================================================================
// SCOPE ACTION REQUESTS (Phase 6)
// =====================================================================

export interface AnalyticsCountScopeRequest {
  filterGroups: FilterGroupRequest[]
  profileNames?: string[]
  polygonWkt?: string
  bBox?: BoundingBox
  scope: ScopeType
  thresholds?: Record<string, number>
}

export interface AnalyticsApplyTagsRequest {
  filterGroups: FilterGroupRequest[]
  profileNames?: string[]
  polygonWkt?: string
  bBox?: BoundingBox
  scope: ScopeType
  tagIds: string[]
  thresholds?: Record<string, number>
}

export interface AnalyticsCreateTurfRequest {
  filterGroups: FilterGroupRequest[]
  profileNames?: string[]
  polygonWkt?: string
  bBox?: BoundingBox
  scope: ScopeType
  thresholds?: Record<string, number>
  campaignId: string
  label: string
}

export interface AnalyticsCreateTurfResponse {
  turfMapId: string
  merged: boolean
  contactCount: number
}
